import { client } from '../apollo';
import { User } from '../containers/shared/auth/AuthContext';
import { getUserRole } from '../containers/shared/auth/rules';
import {
  ALL_CIRCUITS,
  ALL_COUNTRIES,
  ALL_DISTRICTS,
  ALL_DISTRICT_MUNICIPALITIES,
  ALL_EDUCATION_SECTORS,
  ALL_FUNDING_CLASSIFICATIONS,
  ALL_GENDERS,
  ALL_GRADES,
  ALL_HOME_LANGUAGES,
  ALL_LOCAL_MUNICIPALITIES,
  ALL_POPULATION_GROUPS,
  ALL_PROVINCES,
  ALL_REGIONS,
  ALL_REGISTRATION_STATUSES,
  ALL_SCHOOL_PHASES,
  ALL_SCHOOL_SPECIALISATION_GROUPS,
  ALL_SCHOOL_TYPES,
  ALL_STAKEHOLDER_TYPES,
  GET_DEFAULT_INSTITUTION_LAYOUT,
  GET_INSTITUTIONS_BY_EXACT_FIELD_MATCH,
  GET_PERSON,
  IS_VALID_EMIS,
  ALL_REASON_FOR_TERMINATIONS,
  ALL_SUBJECTS,
  // GET_REGISTRATION_OFFICES,
} from '../graphql';
// import { formatOrganisationName } from './format';

const adminRoles = ['ADMIN', 'SUPERADMIN'];
const funderRoles = [...adminRoles, 'FUNDER_OWNER', 'FUNDER_ADMIN', 'FUNDER_MEMBER', 'FUNDER_GUEST'];
const institutionRoles = [
  ...adminRoles,
  'INSTITUTION_OWNER',
  'INSTITUTION_ADMIN',
  'INSTITUTION_MEMBER',
  'INSTITUTION_FIELD_MEMBER',
  'INSTITUTION_GUEST',
];
const spRoles = [
  ...adminRoles,
  'SERVICE_PROVIDER_OWNER',
  'SERVICE_PROVIDER_ADMIN',
  'SERVICE_PROVIDER_MEMBER',
  'SERVICE_PROVIDER_FIELD_MEMBER',
  'SERVICE_PROVIDER_GUEST',
];

export const validatePortalFromURL = () => {
  const path = window.location.pathname;
  const portalURL = ['/admin', '/funder', '/institution', '/service-provider'].filter(portalPath =>
    path.startsWith(portalPath)
  );

  return portalURL[0];
};

export const validateAccountAndPortal = (user: User) => {
  const isAdmin =
    adminRoles.some(role => user.roles.includes(getUserRole(role))) && window.location.pathname.includes('/admin');
  const isFunder =
    funderRoles.some(role => user.roles.includes(getUserRole(role))) && window.location.pathname.includes('/funder');
  const isInstitution =
    institutionRoles.some(role => user.roles.includes(getUserRole(role))) &&
    window.location.pathname.includes('/institution');
  const isServiceProvider =
    spRoles.some(role => user.roles.includes(getUserRole(role))) &&
    window.location.pathname.includes('/service-provider');

  return { isAdmin, isFunder, isInstitution, isServiceProvider };
};

// check for default institution dashboard layout
export const hasDefaultInstitutionDashboard = async () => {
  const response: any = await client
    .query({ query: GET_DEFAULT_INSTITUTION_LAYOUT })
    .then(({ data }: any) => data.layout);
  return Boolean(response);
};

// Work in progress
export const isDate = function(date: string | number) {
  return String(new Date(date)) !== 'Invalid Date' && Date.parse(String(date));
};

/**
 * checks if value is undefined
 * @param object
 */
export const isUndefined = (value: any) => {
  if (typeof value === 'undefined') {
    return true;
  }
  if (Array.isArray(value) && !value[0]) {
    return true;
  }
  return false;
};

export const isValidCircuit = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_CIRCUITS,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.circuits.map((circuit: any) => circuit.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidCountry = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_COUNTRIES,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.countries.map((country: any) => country.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidDistrict = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_DISTRICTS,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.districts.map((district: any) => district.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidDistrictMunicipality = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_DISTRICT_MUNICIPALITIES,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.municipalities.map((municipality: any) => municipality.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidEcdServiceType = async (type: string) => {
  const response: string[] = [
    'ECD centre',
    'Playgroup',
    'After school service partial care facility',
    'Child-minder/day mother',
    'Parental support programme',
    'Mobile ECD programme',
    'Toy library',
  ];
  return response.includes(type);
};

export const isValidEducationSector = async (sector: string) => {
  const response: string[] = await client
    .query({
      query: ALL_EDUCATION_SECTORS,
    })
    .then(({ data }: any) => data.educationSectors.map((sector: any) => sector.name.toLowerCase()));
  return response.includes(sector.toLowerCase());
};

export const isValidEmis = async (emisNumber: number): Promise<boolean> => {
  const response = await client
    .query({
      query: IS_VALID_EMIS,
      variables: {
        emisNumber,
      },
    })
    .then(({ data }: any) => data.isValidEmis);
  return response;
};

export const isValidFundingClass = async (className: string) => {
  const response: string[] = await client
    .query({
      query: ALL_FUNDING_CLASSIFICATIONS,
    })
    .then(({ data }: any) => data.fundingClassifications.map((className: any) => className.name.toLowerCase()));
  return response.includes(className.toLowerCase());
};

export const isValidGender = async (gender: string) => {
  const response: string[] = await client
    .query({
      query: ALL_GENDERS,
    })
    .then(({ data }: any) => data.genders.map((type: any) => type.name.toLowerCase()));
  return response.includes(gender.toLowerCase());
};

export const isValidGrade = async (grade: string) => {
  const response: string[] = await client
    .query({
      query: ALL_GRADES,
    })
    .then(({ data }: any) => data.grades.map((grade: any) => grade.value.toLowerCase()));
  return response.includes(grade.toLowerCase());
};

export const isValidInstitutionName = async (value: string) => {
  const response: string[] = await client
    .query({
      query: GET_INSTITUTIONS_BY_EXACT_FIELD_MATCH,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.institutions.map((institution: any) => institution.name));
  return response.includes(value);
};

export const isValidRegistrationInstitution = async (institution: any, updateOrNew: string) => {
  const response = await client.query({
    query: GET_INSTITUTIONS_BY_EXACT_FIELD_MATCH,
    variables: {
      input: { name: institution },
    },
  });

  if (response && response.data && response.data.institutions && response.data.institutions.length) {
    const existingUnsubmittedReg =
      response.data.institutions[0] && response.data.institutions[0].ecdRegistrations.find((a: any) => !a.submitted);

    if (updateOrNew === 'new') {
      if (existingUnsubmittedReg) {
        return 'Existing Registration found for this institution, please use the update option.';
      } else {
        return '';
      }
    } else if (updateOrNew === 'update') {
      if (!existingUnsubmittedReg) {
        return 'No existing Registration found for this institution, please use the new option.';
      } else {
        return '';
      }
    }
  }

  if (institution) {
    return 'Invalid Institution';
  } else {
    return '';
  }
};

export const isValidInstitutionTrackingCode = async (value: string) => {
  const response: string[] = await client
    .query({
      query: GET_INSTITUTIONS_BY_EXACT_FIELD_MATCH,
      variables: {
        input: { trackingCode: value },
      },
    })
    .then(({ data }: any) => data.institutions.map((institution: any) => institution.name.toLowerCase()));
  return !response.includes(value.toLowerCase());
};

export const isValidLanguage = async (language: string) => {
  const response: string[] = await client
    .query({
      query: ALL_HOME_LANGUAGES,
    })
    .then(({ data }: any) => data.homeLanguages.map((homeLanguage: any) => homeLanguage.name.toLowerCase()));
  return response.includes(language.toLowerCase());
};

export const isValidLocalMunicipality = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_LOCAL_MUNICIPALITIES,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) =>
      data.municipalities.map((municipality: any) => municipality.localMunicipality.toLowerCase())
    );
  return response.some(municipality => municipality.includes(value.toLowerCase()));
};

export const isValidNewOrRenewalValue = async (value: string) => {
  const response: string[] = ['new', 'renewal'];

  return response.includes(value.toLowerCase());
};

export const isValidNearestPointOfInterest = async (value: string) => {
  const response: string[] = [
    'police station',
    'school',
    'clinic',
    'post office',
    'church',
    'tuckshop',
    'sangoma',
    'other',
  ];

  return response.includes(value.toLowerCase());
};

export const isValidFoodProvider = async (values: string) => {
  const response: string[] = ['ecd programme', 'parents', 'government', 'ngo or npo', 'other'];
  const valuesArray = values.split(',');
  let valid = true;
  if (valuesArray.length > 1) {
    valid = !!valuesArray.find((a: any) => !response.includes(a.toLowerCase()));
  } else if (valuesArray.length === 1) {
    valid = response.includes(valuesArray[0].toLowerCase());
  }

  return valid;
};

// export const isValidRegistrationOffice = async (values: string) => {
//   const response: any = await client
//     .query({
//       query: GET_REGISTRATION_OFFICES,
//       variables: {
//         input: {},
//       },
//     })
//     .then(({ data }: any) => data.institutions.edges.map((a: any) => formatOrganisationName(a.node).toLowerCase()));

//   let valid = true;
//   const valuesArray = values.split(',');
//   if (valuesArray.length > 1) {
//     valid = !!valuesArray.find((a: any) => !response.includes(a.toLowerCase()));
//   } else if (valuesArray.length === 1) {
//     valid = response.includes(valuesArray[0].toLowerCase());
//   }

//   return valid;
// };

export const isValidYear = async (value: string) => {
  return Number.isInteger(Number(value)) && Number(value) > 1950 && Number(value) < 3000;
};

export const isValidPeopleId = async (value: string) => {
  const response: any = await client.query({
    query: GET_PERSON,
    variables: {
      id: value,
    },
  });
  return !!response.data.person;
};

export const isValidPopulationGroup = async (group: string) => {
  const response: string[] = await client
    .query({
      query: ALL_POPULATION_GROUPS,
    })
    .then(({ data }: any) => data.populationGroups.map((populationGroup: any) => populationGroup.name.toLowerCase()));
  return response.includes(group.toLowerCase());
};

export const isValidProvince = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_PROVINCES,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.provinces.map((province: any) => province.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidRegion = async (value: string) => {
  const response: string[] = await client
    .query({
      query: ALL_REGIONS,
      variables: {
        input: { name: value },
      },
    })
    .then(({ data }: any) => data.regions.map((region: any) => region.name.toLowerCase()));
  return response.includes(value.toLowerCase());
};

export const isValidRegistrationStatus = async (status: string) => {
  const response: string[] = await client
    .query({
      query: ALL_REGISTRATION_STATUSES,
    })
    .then(({ data }: any) => data.registrationStatuses.map((status: any) => status.name.toLowerCase()));
  return response.includes(status.toLowerCase());
};

export const isValidSchoolPhase = async (phase: string) => {
  const response: string[] = await client
    .query({
      query: ALL_SCHOOL_PHASES,
    })
    .then(({ data }: any) => data.schoolPhases.map((phase: any) => phase.name.toLowerCase()));
  return response.includes(phase.toLowerCase());
};

export const isValidSchoolSpecializationGroup = async (group: string) => {
  const response: string[] = await client
    .query({
      query: ALL_SCHOOL_SPECIALISATION_GROUPS,
    })
    .then(({ data }: any) => data.schoolSpecialisationGroups.map((group: any) => group.name.toLowerCase()));
  return response.includes(group.toLowerCase());
};

export const isValidSchoolType = async (type: string) => {
  const response: string[] = await client
    .query({
      query: ALL_SCHOOL_TYPES,
    })
    .then(({ data }: any) => data.schoolTypes.map((type: any) => type.name.toLowerCase()));
  return response.includes(type.toLowerCase());
};

export const isValidType = async (type: string) => {
  const response: string[] = await client
    .query({
      query: ALL_STAKEHOLDER_TYPES,
    })
    .then(({ data }: any) => data.stakeholderTypes.map((type: any) => type.name.toLowerCase()));
  return response.includes(type.toLowerCase());
};

export const isValidTypeAndOperation = async (record: any, updateOrNew?: string) => {
  const valid = await isValidType(record.type);

  if (!valid) {
    return 'Must be valid type';
  } else if (updateOrNew === 'update' && !record.id) {
    return 'System ID is required when updating';
  } else {
    return '';
  }
};

export const isValidVerifiedValue = async (value: string) => {
  const response: string[] = ['YES', 'NO'];

  return response.includes(value.toUpperCase());
};

export const isValidReasonForExit = async (reason: string) => {
  const response: string[] = await client
    .query({
      query: ALL_REASON_FOR_TERMINATIONS,
    })
    .then(({ data }: any) => data.reasonForTerminations.map((reason: any) => reason.name.toLowerCase()));
  return response.includes(reason.toLowerCase());
};

export const isValidSubjectCode = async (code: string) => {
  const response = await client
    .query({
      query: ALL_SUBJECTS,
    })
    .then(({ data }: any) => data.subjects.map((code: any) => code.nationalCode.toString().toLowerCase()));
  return response.includes(code.toLowerCase());
};

export const isValidDate = (date: string) => {
  if (date && !Number(date) && new Date(date).toString() !== 'Invalid Date') {
    return true;
  }
  return false;
};

export const isValidDateFormat = (date: string) => {
  // Define a regular expression pattern for the desired format
  // const dateFormatPattern = /^\d{4}\/\d{2}\/\d{2}$/;

  // Test if the input string matches the pattern
  // return dateFormatPattern.test(date);
  // if (dateFormatPattern.test(date)) {
  // return isValidDate(date);
  // } else {
  //   return false;
  // }

  return isValidDate(date);
};

// Numeric upload date formats accepted by the monitoring-form parser:
// yyyy-mm-dd / yyyy/mm/dd or dd-mm-yyyy / dd/mm/yyyy (also dd-mm-yy).
// Non-numeric formats such as "01 Jan 2026" are rejected.
export const isValidUploadDateFormat = (date: string) => {
  if (!date) {
    return false;
  }
  const cleaned = String(date)
    .trim()
    .replace(/\//g, '-');
  const parts = cleaned.split('-');
  if (parts.length !== 3 || !parts.every(p => /^\d+$/.test(p))) {
    return false;
  }

  let year: number;
  let month: number;
  let day: number;

  if (parts[0].length === 4) {
    // yyyy-mm-dd
    year = Number(parts[0]);
    month = Number(parts[1]);
    day = Number(parts[2]);
  } else {
    // dd-mm-yyyy (or dd-mm-yy)
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2].length === 2 ? `20${parts[2]}` : parts[2]);
  }

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    return false;
  }
  return true;
};

export const validateNumbers = (value: number) => {
  let error;
  if (value && value < 0) {
    error = 'Value cannot be negative.';
  }
  return error;
};

export const validateEmail = (value: string) => {
  let error;
  if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    error = 'Invalid email address';
  }
  return error;
};

export const validateContactNumber = (value: any) => {
  let error;
  if (
    value &&
    !/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/i.test(
      value
    )
  ) {
    error = 'Invalid contact number';
  }
  return error;
};

function digits(n: any): any {
  return Array.from(n.toString && n.toString());
}

const isValidSAIDNumber = (idInput: string) => {
  if (idInput === null || typeof idInput === 'undefined') {
    return false;
  }

  const id = digits(idInput);

  if (id.length !== 13) return false;

  // Add up all the even digits (excluding the last)
  const a: any = id.slice(0, 12).reduce((sum: any, d: any, i: any) => {
    return i % 2 ? sum : sum + Number(d);
  }, 0);

  // Concatenate all the odd digits and multiply by two
  const b = Number(id.reduce((s: any, d: any, i: any) => (i % 2 ? s + d.toString() : s), '')) * 2;

  // Add `a` to the sum of all the digits in `b`
  const c: any = a + digits(b).reduce((sum: any, d: any) => sum + Number(d), 0);

  // Subtract the last digit of `c` from 10
  const d = 10 - digits(c).pop();

  // Validate by comparing the last digit of `d` to the last digit of the id number
  return digits(d).pop() === id[12];
};

export const validateSAIDNumber = (value: any, field?: any) => {
  if (value && !isValidSAIDNumber(value)) {
    return 'Invalid SA ID Number. Please capture valid SA ID Number.';
  }
  return;
};

const ADMIN_ROLES = ['ADMIN', 'SUPERADMIN', 'INSTITUTION_ADMIN', 'SERVICE_PROVIDER_ADMIN', 'FUNDER_ADMIN'] as const;

export const isClientAdminRole = (user: any): boolean => {
  return user.roles?.some((role: any) => ADMIN_ROLES.includes(role)) ?? false;
};
